import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { CircularProgress, Box } from "@material-ui/core";
import { useSnackbarContext } from "../../commons/Snackbar/Snackbar";
import ProductsTable from "./ProductsTable";
import ProductStudio from "./ProductStudio";
import { listProducts, updateProduct, getCheckoutCatalog, apiErrorMessage } from "./checkoutProductsApi";

const CheckoutProductsPage = () => {
  const permissions = useSelector((state) => state.login.permissions);
  const canWrite = !!permissions?.find((p) => p.label === "checkout-products")?.write;

  const { showSnackbar } = useSnackbarContext();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [catalog, setCatalog] = useState({ families: [] });
  const [filters, setFilters] = useState({ type: "", status: "" });
  const [createOpen, setCreateOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const fetchList = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.type) params.type = activeFilters.type;
      if (activeFilters.status) params.status = activeFilters.status;
      const data = await listProducts(params);
      setProducts(Array.isArray(data) ? data : []);
    } catch (err) {
      showSnackbar(apiErrorMessage(err, "Failed to load products"), "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => { fetchList(filters); }, [filters, fetchList]);

  useEffect(() => {
    getCheckoutCatalog()
      .then((data) => setCatalog(data || { families: [] }))
      .catch((err) => showSnackbar(apiErrorMessage(err, "Failed to load plan options"), "error"));
  }, [showSnackbar]);

  const handleSaved = async () => { await fetchList(filters); };
  const closeStudio = () => { setCreateOpen(false); setEditProduct(null); };

  // Quick activate/deactivate from the list, without opening the editor.
  const toggleStatus = async (p) => {
    const next = p.status === "active" ? "archived" : "active";
    try {
      await updateProduct(p._id, { status: next });
      showSnackbar(next === "active" ? "Product activated" : "Product deactivated", "success");
      await fetchList(filters);
    } catch (err) {
      showSnackbar(apiErrorMessage(err), "error");
    }
  };

  // Create and edit share the full-page studio, which replaces the list so the
  // app sidebar stays visible. Keyed by product id so switching rows remounts it
  // with fresh prefilled state. onSaved only refreshes the list; the studio
  // decides whether to close (create) or stay open (edit).
  if (createOpen || editProduct) {
    return (
      <ProductStudio
        key={editProduct?._id || "create"}
        product={editProduct}
        catalog={catalog}
        onClose={closeStudio}
        onSaved={handleSaved}
        showSnackbar={showSnackbar}
      />
    );
  }

  return loading ? (
    <Box display="flex" justifyContent="center" alignItems="center" style={{ height: "100vh" }}>
      <CircularProgress />
    </Box>
  ) : (
    <ProductsTable
      products={products}
      filters={filters}
      onFilterChange={setFilters}
      canWrite={canWrite}
      onCreate={() => setCreateOpen(true)}
      onRowClick={(p) => setEditProduct(p)}
      onToggleStatus={toggleStatus}
    />
  );
};

export default CheckoutProductsPage;
